import type { FullResponseWithAnswersLight } from "../types";
import type { UserId } from "@/backend/db/drizzle/types/idTypes";
import type { AgreementRecord } from "@/lib/agreements/types";

const DB_NAME = "response-cache";
const STORE_NAME = "responses";
const DB_VERSION = 1;
// Separate from the IndexedDB database version above: this invalidates
// response-cache records without running an IndexedDB store migration.
// v3: aspectData answers for file-upload items are derived from fileUploadDescription.
// v4: issueSubmissions added to rows; private entries are scoped by user id so
// privileged fields cannot leak to another account in the same browser.
// v5: agreement records travel with the cached response rows.
// v6: open-ended answers are the raw participant messages; isAnalysisPending added.
// v7: aspectData carries the file upload reference-check verdict.
// v9: omit empty aspect entries to keep wide studies within browser memory limits.
// v11: aspectData carries the uploaded file reference for the cell thumbnail,
// withheld under the all-media-redacted flag.
const CACHE_SCHEMA_VERSION = 11;
const CACHE_KEY_PREFIX = `v${CACHE_SCHEMA_VERSION}`;
const CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

let dbPromise: Promise<IDBDatabase> | null = null;

const openDB = (): Promise<IDBDatabase> => {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        request.result.createObjectStore(STORE_NAME);
      };
      request.onsuccess = () => {
        const db = request.result;
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        // Scan keys only so obsolete sampling designs never copy into browser memory.
        const keys = store.getAllKeys();
        keys.onsuccess = () => {
          keys.result
            .filter((key) => {
              if (typeof key !== "string") return false;
              const version = key.match(/^v(\d+):/)?.[1];
              return version ? Number(version) < CACHE_SCHEMA_VERSION : true;
            })
            .forEach((key) => store.delete(key));
        };
        tx.oncomplete = () => resolve(db);
        tx.onabort = () => {
          dbPromise = null;
          db.close();
          reject(
            toCacheError(tx.error, "Failed to remove obsolete response caches")
          );
        };
      };
      request.onerror = () => {
        dbPromise = null;
        reject(new Error(request.error?.message ?? "Failed to open IndexedDB"));
      };
    });
  }
  return dbPromise;
};

export type ResponseCacheData = {
  responses: FullResponseWithAnswersLight[];
  agreementRecords: AgreementRecord[];
  cacheVersion: string | null;
};

type CacheEntry = ResponseCacheData & {
  timestamp: number;
  schemaVersion: number;
};

// Returns null when there is no key to safely cache under (private scope
// with no resolved user); callers must then skip reads and writes.
export const getResponseCacheKey = ({
  surveyId,
  sharingToken,
  dashboardToken,
  userId,
}: {
  surveyId: string;
  sharingToken?: string;
  dashboardToken?: string;
  userId: UserId | null;
}): string | null => {
  if (dashboardToken) {
    return `${CACHE_KEY_PREFIX}:dashboard:${surveyId}:${encodeURIComponent(
      dashboardToken
    )}`;
  }
  if (sharingToken) {
    return `${CACHE_KEY_PREFIX}:share:${surveyId}:${encodeURIComponent(
      sharingToken
    )}`;
  }
  if (!userId) return null;
  return `${CACHE_KEY_PREFIX}:private:${surveyId}:${userId}`;
};

const isCacheKeyForSurvey = (key: IDBValidKey, surveyId: string) => {
  if (typeof key !== "string") return false;
  const [version, scope, keySurveyId] = key.split(":");
  return (
    key === surveyId ||
    (version.startsWith("v") &&
      ["private", "share", "dashboard"].includes(scope) &&
      keySurveyId === surveyId)
  );
};

const toCacheError = (error: unknown, fallbackMessage: string) =>
  error instanceof Error ? error : new Error(fallbackMessage);

const deleteCacheEntry = async (cacheKey: string) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(cacheKey);
  } catch {
    // IndexedDB unavailable
  }
};

export const getCachedResponses = async (
  cacheKey: string
): Promise<ResponseCacheData | null> => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(cacheKey);
    return new Promise((resolve) => {
      request.onsuccess = () => {
        const entry = request.result as CacheEntry | undefined;
        if (!entry) {
          resolve(null);
          return;
        }
        if (entry.schemaVersion !== CACHE_SCHEMA_VERSION) {
          void deleteCacheEntry(cacheKey);
          resolve(null);
          return;
        }
        if (Date.now() - entry.timestamp > CACHE_MAX_AGE_MS) {
          void deleteCacheEntry(cacheKey);
          resolve(null);
          return;
        }
        resolve({
          responses: entry.responses,
          agreementRecords: entry.agreementRecords,
          cacheVersion: entry.cacheVersion ?? null,
        });
      };
      request.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
};

export const updateCache = async (
  cacheKey: string,
  responses: FullResponseWithAnswersLight[],
  cacheVersion: string | null,
  agreementRecords: AgreementRecord[]
) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(
      {
        responses,
        agreementRecords,
        timestamp: Date.now(),
        cacheVersion,
        schemaVersion: CACHE_SCHEMA_VERSION,
      } satisfies CacheEntry,
      cacheKey
    );
  } catch (e) {
    console.warn("Failed to write response cache:", e);
  }
};

export const clearResponseCache = async (surveyId?: string) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    if (surveyId) {
      // A survey can now have multiple cache entries, for example private,
      // shared, dashboard, plus the legacy unscoped surveyId key. Scan the
      // store so survey-level invalidation clears every scoped entry.
      await new Promise<void>((resolve, reject) => {
        const request = store.openCursor();
        request.onsuccess = () => {
          const cursor = request.result;
          if (!cursor) return;
          if (isCacheKeyForSurvey(cursor.key, surveyId)) {
            cursor.delete();
          }
          cursor.continue();
        };
        request.onerror = () =>
          reject(toCacheError(request.error, "Failed to scan response cache"));
        tx.oncomplete = () => resolve();
        tx.onerror = () =>
          reject(toCacheError(tx.error, "Failed to clear response cache"));
      });
    } else {
      store.clear();
    }
  } catch {
    // IndexedDB unavailable
  }
};
